import React from 'react';

import { OptionModel } from '@/lib/models/option/types';
import { EmissionStats } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { tv } from '@/utils/styles';

import { OptionRowAir } from './air';
import { OptionRowOcean } from './ocean';
import { OptionRowRail } from './rail';
import { OptionRowRoad } from './road';

export interface OptionRowProps {
  option: OptionModel;
  mode: SegmentModeType;
  onClickDetails?: () => void;
  // can be used to scale the bar sequence of a row relative to the longest duration of all rows
  maxDurationRatio?: number;
  shouldShowRates?: boolean;
  emissionsStats?: EmissionStats;
}

export type OptionRowComponentProps = Omit<OptionRowProps, 'mode'>;

const componentMap: Record<SegmentModeType, React.ComponentType<OptionRowComponentProps>> = {
  air: OptionRowAir,
  water: OptionRowOcean,
  rail: OptionRowRail,
  road: OptionRowRoad,
  transfer: () => null,
};

const OptionRow = ({
  option,
  mode,
  emissionsStats,
  onClickDetails,
  shouldShowRates,
  maxDurationRatio,
}: OptionRowProps) => {
  const Component = componentMap[mode];

  return (
    <Component
      option={option}
      emissionsStats={emissionsStats}
      shouldShowRates={shouldShowRates}
      onClickDetails={onClickDetails}
      maxDurationRatio={maxDurationRatio}
    />
  );
};

export const sharedOptionRowStyles = tv({
  slots: {
    gridBlock: 'item flex flex-col px-4 py-1 first:pl-0 [&_+.item]:border-l [&_+.item]:border-l-grey-200',
    subText: 'mb-0 mt-2 text-sm text-text-secondary',
    bar: 'w-full rounded-2xl bg-grey-200 px-4 py-0',
    primaryText: 'm-0 text-baseSm font-medium leading-4 text-text-primary',
    vehicleText: 'mb-0 ml-1 mt-1.5 text-text-secondary',
  },
  variants: {
    withGap: {
      true: {
        gridBlock: 'gap-1.5',
      },
    },
    row: {
      true: {
        gridBlock: 'flex-row items-center',
      },
    },
    center: {
      true: {
        gridBlock: 'justify-center',
      },
    },
  },
});

export { OptionRow };
